import { defineStore } from 'pinia'
import router from '@/routers'
import { UserStore } from '@/store/interface/user/index.module'
import { Permission } from '@/types/Permission'
import { Ref, ref } from 'vue'
import { GlobalStore } from '@/store'
import { LoginServise } from '@/api/modules/login'
import { ElMessage } from 'element-plus'
import { DbUserServer } from '@/api/modules/dbUser'
import { DbUser } from '@/api/interface/dbUser.model'
import toast from '@/components/Toast/type/index.module'
import piniaPersistConfig from '@/config/piniaPersist'
import { clearAuthTokens } from '@/auth/jwt'

export const useUserStore = defineStore('UserStore', {
  state: (): Ref<UserStore.Userinfo> => {
    return ref({
      id: 0,
      username: '',
      avatar: '',
      email: '',
      status: 0,
      permissions: [] as Permission[] | undefined,
      roles: [] as string[] | undefined,
      from: '' as string | undefined,
      uuid: '',
      userinfo: {} as DbUser.DbUserinfo
    })
  },

  getters: {
    getId(): number {
      return this.id
    },

    getNickname(): string {
      return this.username
    },

    getAvatar(): string {
      return this.avatar
    },

    getRoles(): string[] | undefined {
      return this.roles
    },

    getPermissions(): Permission[] | undefined {
      return this.permissions
    },

    getFrom(): string | undefined {
      return this.from
    },

    getUserMsg(): DbUser.DbUserinfo {
      return this.userinfo
    }
  },

  actions: {
    removeId() {
      this.id = 0
    },
    isSuperAdmin(): boolean {
      return this.id === 1
    },
    setUsername(username: string) {
      this.username = username
    },

    setId(id: number) {
      this.id = id
    },

    setAvatar(avatar: string) {
      this.avatar = avatar
    },

    setRoles(roles: string[] | undefined) {
      this.roles = roles
    },

    setPermissions(permissions: Permission[] | undefined) {
      this.permissions = permissions
    },

    setEmail(email: string) {
      this.email = email
    },

    setStatus(status: number) {
      this.status = status
    },

    setFrom(form: string | undefined) {
      this.from = form
    },

    setUserinfo(userinfo: DbUser.DbUserinfo) {
      this.userinfo = userinfo
    },

    /**
     * logout
     */
    async logout() {
      const globalStore = GlobalStore()

      const res = await LoginServise.logOut()

      if (res.code === 1) {
        await clearAuthTokens()
        globalStore.removeToken()

        // 重置用户信息
        this.removeId()
        localStorage.removeItem('router')
        // 去除登录标记(防止登录状态下，刷新隐藏页面路由重复添加的标记)
        router.removeRoute('__DYNAMIC_ROUTES_LOADED__')
        toast.success(res.message, 1000)

        this.$reset()
        router.push({ path: '/login' })
      } else {
        toast.error(res.message)
      }
    },

    /**
     * user info
     */
    async getUserInfo() {
      const userStore = useUserStore()

      const res = await DbUserServer.getUserinfo()

      if (!res.data) return

      const { id, username, email, avatar, permissions, roles, status, from } = res.data as DbUser.DbUserinfo

      if (res.code === 1) {
        userStore.setId(id)
        userStore.setUsername(username)
        userStore.setEmail(email)
        userStore.setRoles(roles)
        userStore.setStatus(status)
        userStore.setAvatar(avatar)
        userStore.setPermissions(permissions)
        userStore.setFrom(from)
        userStore.setUserinfo(res.data)
      } else {
        ElMessage.error(res.message)
      }
    }
  },
  persist: piniaPersistConfig(import.meta.env.VITE_META_CODE + 'UserStore')
})
